import React, { useRef, useState } from 'react';
import { Upload, Loader2, X, FileText, Film, ImageIcon } from 'lucide-react';
import { uploadFile, fileUrl } from '../../api';
import { useToast } from '../../hooks/use-toast';

const MAX_MB = 50;

function useUpload(category) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const run = async (file) => {
    if (!file) return null;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast({ title: 'Fișier prea mare', description: `Limita este ${MAX_MB} MB.` });
      return null;
    }
    setBusy(true);
    try {
      const data = await uploadFile(file, category);
      return data;
    } catch (e) {
      toast({ title: 'Încărcare eșuată', description: 'Încearcă din nou.' });
      return null;
    } finally {
      setBusy(false);
    }
  };
  return { busy, run };
}

// Single image field with preview + upload + manual URL
export function ImageField({ label, value, onChange, testid }) {
  const inputRef = useRef(null);
  const { busy, run } = useUpload('foto');
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const data = await run(file);
    if (data) onChange(data.url);
  };
  return (
    <div>
      <div className="text-[12px] text-[#9a9ea6] mb-1.5">{label}</div>
      <div className="flex items-center gap-3">
        <div className="h-16 w-16 rounded-md border border-[#26282e] bg-[#0d0e10] overflow-hidden flex items-center justify-center shrink-0">
          {value ? (
            <img src={fileUrl(value)} alt="preview" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon size={20} className="text-[#4b4f57]" />
          )}
        </div>
        <div className="flex-1">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            data-testid={testid}
            className="btn-ghost rounded-md px-3 py-2 text-[13px] flex items-center gap-1.5"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {busy ? 'Se încarcă...' : 'Încarcă imagine'}
          </button>
          <input
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="sau lipește un URL"
            className="mt-2 w-full text-[12px] bg-[#0d0e10] border border-[#26282e] rounded-md px-2.5 py-1.5 text-[#c7cace] placeholder:text-[#5f636b] focus:outline-none focus:ring-1 focus:ring-[#f5a524]"
          />
        </div>
        <input ref={inputRef} type="file" accept="image/*" hidden onChange={pick} />
      </div>
    </div>
  );
}

const CATS = [
  { key: 'foto', label: 'Foto', accept: 'image/*', icon: ImageIcon },
  { key: 'filme', label: 'Filme', accept: 'video/*', icon: Film },
  { key: 'certificari', label: 'Certificări', accept: 'application/pdf,image/*', icon: FileText },
  { key: 'tehnice', label: 'Tehnice', accept: 'application/pdf,image/*', icon: FileText },
];

function CategoryUploader({ cat, items, onAdd, onRemove }) {
  const inputRef = useRef(null);
  const { busy, run } = useUpload(cat.key);
  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    for (const f of files) {
      const data = await run(f);
      if (data) onAdd({ url: data.url, name: data.name, type: data.type });
    }
  };
  const Icon = cat.icon;
  return (
    <div className="rounded-lg border border-[#26282e] bg-[#0d0e10] p-3">
      <div className="flex items-center justify-between mb-2.5">
        <span className="flex items-center gap-1.5 text-[13px] text-[#c7cace]">
          <Icon size={14} className="text-[#f5a524]" /> {cat.label}
          <span className="text-[11px] text-[#6b6f77]">({items.length})</span>
        </span>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid={`admin-upload-${cat.key}`}
          className="btn-ghost rounded-md px-2.5 py-1.5 text-[12px] flex items-center gap-1.5"
        >
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
          Adaugă
        </button>
        <input ref={inputRef} type="file" accept={cat.accept} multiple hidden onChange={pick} />
      </div>
      {items.length > 0 ? (
        <div className="grid grid-cols-4 gap-2">
          {items.map((it, i) => (
            <div key={i} className="relative group rounded-md border border-[#26282e] bg-[#141518] aspect-square overflow-hidden">
              {it.type === 'image' ? (
                <img src={fileUrl(it.url)} alt={it.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center gap-1 p-1 text-center">
                  {it.type === 'video' ? <Film size={18} className="text-[#8b8f97]" /> : <FileText size={18} className="text-[#8b8f97]" />}
                  <span className="text-[9px] text-[#6b6f77] line-clamp-2 break-all">{it.name}</span>
                </div>
              )}
              <button
                type="button"
                onClick={() => onRemove(i)}
                className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[12px] text-[#6b6f77]">Niciun fișier încă.</p>
      )}
    </div>
  );
}

// Manages the media object { foto[], filme[], certificari[], tehnice[] }
export function MediaManager({ value, onChange }) {
  const media = value || { foto: [], filme: [], certificari: [], tehnice: [] };
  const update = (key, list) => onChange({ ...media, [key]: list });
  return (
    <div className="space-y-3">
      {CATS.map((cat) => {
        const items = media[cat.key] || [];
        return (
          <CategoryUploader
            key={cat.key}
            cat={cat}
            items={items}
            onAdd={(item) => update(cat.key, [...items, item])}
            onRemove={(i) => update(cat.key, items.filter((_, idx) => idx !== i))}
          />
        );
      })}
    </div>
  );
}
